import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
} from "react";
import { Company, Project } from "src/components/Projects/types";
import { RequestingCompany } from "../components/Form/types";
import { RequestFilter } from "../components/Premises/types";
import { routes } from "src/Routes/RouteConstants";

export interface IContext {
  project?: Project;
  requestingCompany?: RequestingCompany;
  error?: Error;
  user?: UserInfo;
  featureFlags: FeatureFlags;
  premiseDashboardFilters: RequestFilter;
  responsiveGuardExceptions: string[];
}

export interface FeatureFlags {
  openChangeRequests: boolean;
  enableResponsiveGuard: boolean; // Enable or disable the responsive guard
  enableEditProject: boolean;
  enableRemoveSupplyForNaturalGas: boolean; // May enable this for some stories in LOC-781
  showCloseButtonInContextMenu?: boolean; // Enable or disable the close button in context menu
}

export interface UserInfo {
  first_name: string;
  last_name: string;
  id: string;
  role: string;
  main_company: Company;
  email: string;
  permissions: string[];
}

// Defaults
const defaultValue: IContext = {
  featureFlags: {
    openChangeRequests: true,
    enableResponsiveGuard: true,
    enableEditProject: false,
    enableRemoveSupplyForNaturalGas: true,
    showCloseButtonInContextMenu: true,
  },
  premiseDashboardFilters: {},
  // Subject to adjust when more pages are responsively styled
  responsiveGuardExceptions: [
    routes.callback,
    routes.projects,
    routes.premisesByProject,
    routes.premises,
    routes.changes,
    routes.drafts,
    routes.tasks,
    routes.newPremise,
    routes.draftPremise,
    routes.claim,
    routes.changeRequest,
    routes.draftChangeRequest,
    // TODO uncomment below in https://origindd.atlassian.net/browse/LOC-1149
    // routes.reports,
    routes.removeSupply,
    routes.draftRemoveSupply,
    routes.draftRemoveSupply,
    routes.publicRemoveSupply,
    routes.publicRemoveSupplySuccess,
    routes.publicEICForm,
  ],
};

export type ContextUpdate = Partial<Omit<IContext, "featureFlags">> & {
  featureFlags?: Partial<FeatureFlags>;
};

export interface IAppContext {
  context: IContext;
  setContext(context: ContextUpdate): void;
}

export const AppContext = createContext<IAppContext>({
  context: defaultValue,
  setContext: /* istanbul ignore next */ () => undefined,
});

export const mergeContext =
  ({ setContext, context }: IAppContext) =>
  (nextContext: Partial<IContext>): void =>
    setContext({
      ...context,
      ...nextContext,
    });

export const useAppContext = (): IAppContext =>
  useContext<IAppContext>(AppContext);

export const AppContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [context, rawSetContext] = useState<IContext>(defaultValue);

  const setContext = useCallback((update: ContextUpdate) => {
    rawSetContext((prev) => ({
      ...prev,
      ...update,
      featureFlags: {
        ...prev.featureFlags,
        ...update.featureFlags,
      },
    }));
  }, []);

  return (
    <AppContext.Provider value={{ context, setContext }}>
      {children}
    </AppContext.Provider>
  );
};

const AppContextConsumer = AppContext.Consumer;
export { AppContextConsumer };
